import { zodResolver } from "@hookform/resolvers/zod";
import { TFunction } from "i18next";
import { Loader2, Save, X } from "lucide-react";
import { useForm, UseFormSetError } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { getCampaignsPath } from "../../utils/navigation";

import { Button } from "@/components/ui/v2/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/v2/form";
import { Input } from "@/components/ui/v2/input";
import { SegmentSelect } from "@/features/segments/components/SegmentSelect";
import { AddCampaignRequest } from "@/types/request/AddCampaignRequest";
import { UpdateCampaignRequest } from "@/types/request/UpdateCampaignRequest";
import { CampaignTypeDTO } from "@/types/response/CampaignType";

// Form validation schema factory
const createCampaignSchema = (t: TFunction) =>
  z.object({
    name: z
      .string()
      .min(1, {
        message: t("errors.requiredField", { fieldName: t("common.name") }),
      })
      .min(2, {
        message: t("errors.fieldMin", { fieldName: t("common.name") }),
      })
      .max(32, {
        message: t("errors.fieldMax", { fieldName: t("common.name") }),
      })
      .regex(/^[\p{L}0-9\-_ /]+$/u, {
        message: t("errors.invalidCharacters", { fieldName: t("common.name") }),
      }),
    segmentId: z.string().min(1, {
      message: t("errors.requiredField", { fieldName: t("common.segment") }),
    }),
  });

export type CampaignFormContentData = {
  name: string;
  segmentId: string;
  clientId?: string;
};

// Base props shared by both add and edit forms
type BaseCampaignFormContentProps = {
  clientId?: string;
  campaign?: CampaignTypeDTO;
  campaignId?: string;
  isSubmitting?: boolean;
  onSuccess?: () => void;
};

// Discriminated union: create form vs edit form
type CreateCampaignFormContentProps = BaseCampaignFormContentProps & {
  type: "create";
  clientId?: string; // Optional for managers creating campaigns without clientId
  onSubmitForm?: (
    data: AddCampaignRequest,
    setError?: UseFormSetError<CampaignFormContentData>
  ) => void;
};

type EditCampaignFormContentProps = BaseCampaignFormContentProps & {
  type: "edit";
  campaignId: string;
  campaign: CampaignTypeDTO;
  onSubmitForm?: (data: UpdateCampaignRequest) => void;
};

type CampaignFormContentProps =
  | CreateCampaignFormContentProps
  | EditCampaignFormContentProps;

const CampaignFormContent = (props: CampaignFormContentProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const methods = useForm<CampaignFormContentData>({
    resolver: zodResolver(createCampaignSchema(t)),
    mode: "onChange",
    defaultValues: {
      name: props.campaign?.name ?? "",
      segmentId: props.campaign?.segmentId ?? "",
      clientId: props.clientId ?? "",
    },
  });

  const { handleSubmit, setError } = methods;

  const onSubmit = handleSubmit(
    (formData) => {
      if (props.type === "edit") {
      // At this point, TypeScript knows props is EditCampaignFormContentProps
        if (!props.onSubmitForm || !props.campaignId) {
          return;
        }

        const payload: UpdateCampaignRequest = {
          id: props.campaignId,
          name: formData.name,
          segmentId: formData.segmentId,
          ...(props.clientId ? { clientId: props.clientId } : {}),
        };

        props.onSubmitForm(payload);
        return;
      }

      // Create form - clientId is optional (managers can create campaigns without clientId)
      if (!props.onSubmitForm) {
        return;
      }

      const payload: AddCampaignRequest = {
        name: formData.name,
        segmentId: formData.segmentId,
        ...(props.clientId ? { clientId: props.clientId } : {}),
      };

      props.onSubmitForm(payload, setError);
    }
  );

  const handleCancelButton = () => {
    if (props.onSuccess) {
      props.onSuccess();
    } else {
      navigate(getCampaignsPath(props.clientId));
    }
  };
  return (
    <Form {...methods}>
      <form onSubmit={onSubmit} className="max-w-lg">
        <div className="space-y-6">
          {props.type === "create" && (
            <FormField
              control={methods.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("common.name")}</FormLabel>
                  <Input
                    {...field}
                    placeholder={t("admin.campaigns.nameCampaign")}
                  />
                  <FormDescription>
                    {t("admin.campaigns.nameDescription")}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={methods.control}
            name="segmentId"
            render={({ fieldState }) => {
              return (
                <FormItem>
                  <FormControl>
                    <SegmentSelect
                      fieldName="segmentId"
                      label={t("common.segment")}
                      placeholder={t("admin.campaigns.selectSegment")}
                      clientId={props.clientId}
                      useFormLabel={true}
                      aria-invalid={!!fieldState.error}
                    />
                  </FormControl>
                  <FormDescription className="mt-2">
                    {t("admin.campaigns.segmentDescription")}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>

        <div className="flex items-center justify-start gap-2 mt-6">
          <Button type="submit" disabled={props.isSubmitting}>
            {props.isSubmitting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Save className="size-4" />
            )}
            {props.type === "edit"
              ? t("common.save")
              : t("admin.campaigns.createCampaign")}
          </Button>
          <Button type="button" variant="outline" onClick={handleCancelButton}>
            <X className="size-4" />
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default CampaignFormContent;
